/**
 * DashboardCallFacilitator Component
 * Facilitator-specific Whereby call container with dedicated session resolution
 */

import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { DashboardCallEmptyState } from './DashboardCallEmptyState'
import { WherebyEmbed } from './WherebyEmbed'
import { WherebyCallBlockedPanel } from '../../../features/gamer/components/whereby/WherebyCallBlockedPanel'
import { useWherebyCallOwnership } from '../../hooks/useWherebyCallOwnership'
import { buildEmbeddedCallScope } from '../../hooks/wherebyCallScopes'
import { useUser } from '../../../hooks/useUser'
import useAppState from '../../../hooks/useAppState'
import {
  useTeamWherebyMeeting,
  useActivityWherebyMeeting,
  useCreateWherebyMeeting,
  useEnsureActivityWherebyMeeting
} from '../../../apiLayer'
import config from '../../../Config'
import { useSessionIdFacilitator } from '../../../hooks/useSessionIdFacilitator'
import { resolveWherebyScope } from '../../../../../pure-js/libs/resolveWherebyScope'
import { getWherebyCallDisplayName } from '../../../../../pure-js/libs/isWherebyBotParticipant'

interface DashboardCallFacilitatorProps {
  width?: string | number
  height?: string | number
}

export const DashboardCallFacilitator: React.FC<DashboardCallFacilitatorProps> = ({ width = '100%', height }) => {
  const { data: user } = useUser()
  const { state: appState } = useAppState()
  const location = useLocation()
  const { teamId, sprintId, activityId, activityType, isLoading: isLoadingSession } = useSessionIdFacilitator()

  // Shared activity-vs-team scope decision (resolveWherebyScope) — same rule as the gamer call, so the
  // facilitator joins the same per-activity room as the players (never a stray team room for a game).
  const { isActivityScope, teamRoomTeamId } = resolveWherebyScope({
    sprintId,
    activityId,
    activityType,
    teamId
  })
  const activityQuery = useActivityWherebyMeeting(isActivityScope ? { teamId, activityId } : undefined)
  // An activity with a room of its own (a game, a debrief, a sprint call) never lands in the team room.
  const teamQuery = useTeamWherebyMeeting(teamRoomTeamId)

  const activeMeeting = useMemo(
    () => (isActivityScope ? activityQuery.data : teamQuery.data)?.data ?? undefined,
    [isActivityScope, activityQuery.data, teamQuery.data]
  )
  const isLoadingMeeting = isActivityScope ? activityQuery.isLoading : teamQuery.isLoading
  const refetchMeeting = isActivityScope ? activityQuery.refetch : teamQuery.refetch

  const isLoading = isLoadingSession || (teamId ? isLoadingMeeting : false)

  const room = activeMeeting?.hostRoomUrl || activeMeeting?.roomUrl
  // The signed-in user stands in when the user read came back empty.
  const displayName = getWherebyCallDisplayName(user) || getWherebyCallDisplayName(appState.user)

  const createWherebyMeetingMutation = useCreateWherebyMeeting()
  const ensureActivityMutation = useEnsureActivityWherebyMeeting()

  // One live Whereby connection per user: same claim as every other call surface, so a facilitator can
  // never sit in two rooms at once.
  const ownershipScope = useMemo(
    () =>
      buildEmbeddedCallScope({
        teamId,
        sprintId,
        activityId,
        activityType,
        isActivityScope,
        currentPathname: location.pathname
      }),
    [teamId, sprintId, activityId, activityType, isActivityScope, location.pathname]
  )
  const ownership = useWherebyCallOwnership({ enabled: Boolean(room), scope: ownershipScope })

  useEffect(() => {
    if (!config.enableCall) return
    if (isLoading || !teamId) return
    if (activeMeeting) return

    if (isActivityScope && sprintId && activityId) {
      ensureActivityMutation.mutate({ teamId, sprintId, activityId }, { onSuccess: () => void refetchMeeting() })
    } else if (teamRoomTeamId) {
      createWherebyMeetingMutation.mutate(
        { roomNamePrefix: teamRoomTeamId, teamId: teamRoomTeamId },
        { onSuccess: () => void refetchMeeting() }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, sprintId, activityId, isLoading, activeMeeting])

  if (!teamId || !room) {
    return <DashboardCallEmptyState teamId={teamId} />
  }

  // No embed until the person's own name is known.
  if (!displayName) {
    return null
  }

  // The call is live somewhere else (another tab/device, or the floating call in this tab) — ask, never
  // connect on top of it.
  if (!ownership.canConnect) {
    return (
      <WherebyCallBlockedPanel
        status={ownership.status}
        onConfirmTakeover={ownership.confirmTakeover}
        onRetry={ownership.retry}
      />
    )
  }

  return <WherebyEmbed room={room} displayName={displayName} width={width} height={height} />
}

export default DashboardCallFacilitator
